import { hasValue, isFunction } from 'swipeclock-frontend';

import { analytics } from 'lib/analytics';
import config from 'data/config';
import { formatDateForURL } from 'helpers/dates';
import { route } from 'lib/router';
import { throwErrorWithStatusCode } from 'helpers/errorHelper';

export function getURL({ token, anchor }) {
  if (anchor?.url) {
    return { url: anchor.url };
  }
  /* eslint-disable max-len */
  const routeOptions = {
    [route.biometrics]: (jwt) =>
      `${config.timeuiURL}/biometrics.html?jwt=${jwt}`,
    [route.clockPrompts]: (jwt) =>
      `${config.timeLaborDailyURL}/Accountant/ClockPromptSetup.aspx?jwt=${jwt}&embedded=true&embedas=tc`,
    [route.hardwareClocks]: (jwt) =>
      `${config.timeuiURL}/hardwareClocks.html?jwt=${jwt}`,
    [route.companyInfo]: (jwt) =>
      `${config.masterDataUIURL}/companyInfo.html?jwt=${jwt}`,
    [route.deductions]: (jwt) =>
      `${config.masterDataUIURL}/deductions.html?jwt=${jwt}`,
    [route.employeeTypes]: (jwt) =>
      `${config.masterDataUIURL}/employeeTypes.html?jwt=${jwt}`,
    [route.geofence]: (jwt) =>
      `${config.geofenceManagementURL}/fence?jwt=${jwt}`,
    [route.groups]: (jwt) =>
      `${config.timeLaborDailyURL}/Client/EmployeeGroups.aspx?jwt=${jwt}&embedded=true&embedas=tc`,
    [route.integrations]: (jwt) =>
      `${config.partnerURL}/index-clientintegrationsettings.html?jwt=${jwt}`,
    [route.organizationSetup]: (jwt) =>
      `${config.masterDataUIURL}/organizationData.html?jwt=${jwt}`,
    [route.payrollExport]: (jwt) =>
      `${config.timeLaborDailyURL}/Client/DownloadActivity.aspx?jwt=${jwt}&embedded=true&embedas=tc`,
    [route.people]: (jwt) =>
      `${config.masterDataUIURL}/employees.html?jwt=${jwt}`,
    [route.punchCategories]: (jwt) =>
      `${config.timeuiURL}/punchCategories.html?jwt=${jwt}`,
    [route.settings]: (jwt) => `${config.timeuiURL}/settings.html?jwt=${jwt}#/`,
    [route.settingsSchedule]: (jwt) => `${config.scheduleURL}?jwt=${jwt}`,
    [route.settingsTimecard]: (jwt) =>
      `${config.timeuiURL}/settings.html?jwt=${jwt}#/timecard`,
    [route.settingsTimeclock]: (jwt) =>
      `${config.timeuiURL}/settings.html?jwt=${jwt}#/timeclock`,
    [route.settingsBreaksMeals]: (jwt) =>
      `${config.timeuiURL}/settings.html?jwt=${jwt}#/breaks-and-meals`,
    [route.settingsTimeoff]: (jwt) =>
      `${config.timeuiURL}/settings.html?jwt=${jwt}#/timeoff`,
    [route.settingsOvertime]: (jwt) =>
      `${config.timeuiURL}/settings.html?jwt=${jwt}#/overtime`,
    [route.settingsHolidays]: (jwt) =>
      `${config.timeuiURL}/holidays.html?jwt=${jwt}`,
    [route.schedule]: (jwt) =>
      `${config.timeuiURL}/scheduling.html?jwt=${jwt}#/managerSchedule`,
    [route.shiftSwaps]: (jwt) =>
      `${config.timeuiURL}/scheduling.html?jwt=${jwt}#/shiftSwaps/pending`,
    [route.employeeSchedule]: (jwt) =>
      `${config.timeuiURL}/scheduling.html?jwt=${jwt}#/employeeSchedule`,
    [route.employeeOpenShifts]: (jwt) =>
      `${config.timeuiURL}/scheduling.html?jwt=${jwt}#/employeeSchedule?subFeature=openShifts`,
    [route.timeCard]: (jwt) =>
      `${config.timeuiURL}/timecard.html?jwt=${jwt}#/manage`,
    [route.timeCardDaily]: (jwt) => {
      const today = formatDateForURL();
      return `${config.timeLaborDailyURL}/Client/TimeCard.aspx?jwt=${jwt}&embedded=true&BeginDate=${today}-18&EndDate=${today}`;
    },
    [route.timeOff]: (jwt) =>
      `${config.timeuiURL}/timeoff.html?jwt=${jwt}#/requests/pending`,
    [route.myTimeOff]: (jwt) =>
      `${config.timeuiURL}/timeoff.html?jwt=${jwt}#/mytimeoff`,
    [route.timeReports]: (jwt) =>
      `${config.masterDataReportsURL}/Menu/MasterDataReports.aspx?jwt=${jwt}&embedded=true`,
    [route.companies]: (jwt) =>
      `${config.masterDataUIURL}/companies.html?jwt=${jwt}`,
    [route.partnerHardwareClocks]: (jwt) =>
      `${config.timeuiURL}/hardwareClocks.html?jwt=${jwt}&allSites=true`,
    [route.partnerIntegration]: (jwt) =>
      `${config.partnerIntegrationEmbedURL}?jwt=${jwt}&hidePortalLink=true`,
    [route.partnerProducts]: (jwt) =>
      `${config.masterDataUIURL}/products.html?jwt=${jwt}`,
    [route.partnerPricing]: (jwt) =>
      `${config.masterDataUIURL}/pricing.html?jwt=${jwt}`,
    [route.partnerFGAdmin]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/fgAdministration.html?jwt=${jwt}`,
    [route.supportCenter]: () => 'https://supportcenter.payrollservers.info/',
    [route.marketingContent]: () => 'https://partner.swipeclock.com/',
    [route.timeui]: (jwt) =>
      `${config.timeuiURL}/payrollExport.html?jwt=${jwt}`,
    [route.dashboard]: (jwt) => `${config.timeuiURL}/dashboard.html?jwt=${jwt}`,
    [route.finalizePayPeriod]: (jwt) =>
      `${config.timeuiURL}/finalizePayPeriod.html?jwt=${jwt}`,
    [route.partnerUserManagement]: (jwt) =>
      `${config.timeLaborDailyURL}/Client/LoginList.aspx?jwt=${jwt}&mode=acct`,
    [route.twpPortal]: (jwt) =>
      `${config.timeLaborDailyURL}/Login.aspx?jwt=${jwt}&dir=cl`,
    [route.employeesList]: (jwt) =>
      `${config.masterDataUIURL}/employees.html?jwt=${jwt}&hidenav=true`,
    [route.employeeRecord]: (jwt, routeAnchor) =>
      `${config.masterDataUIURL}/employees.html?jwt=${jwt}&hidenav=true#/employee/${routeAnchor?.employeeId}`,
    [route.organizationChart]: (jwt) =>
      `${config.masterDataUIURL}/employees.html?jwt=${jwt}&hidenav=true#/orgChart`,
    [route.bulkAddAndEdit]: (jwt) =>
      `${config.masterDataUIURL}/employees.html?jwt=${jwt}#/bulkUpload`,
    [route.peopleTabAccess]: (jwt) =>
      `${config.masterDataUIURL}/peopleTabAccess.html?jwt=${jwt}`,
    [route.employeeInvites]: (jwt) =>
      `${config.masterDataUIURL}/employeeInvites.html?jwt=${jwt}`,
    [route.accruals]: (jwt) => `${config.timeuiURL}/accruals.html?jwt=${jwt}`,
    [route.generalInfo]: (jwt) =>
      `${config.masterDataUIURL}/generalInfo.html?jwt=${jwt}`,
    [route.essSettings]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/essSettings.html?jwt=${jwt}&firmLevel=true`,
    [route.onboardSettings]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/onboardSettings.html?jwt=${jwt}&firmLevel=true`,
    [route.documentSettings]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/documentSettings.html?jwt=${jwt}&firmLevel=true`,
    [route.unmatchedPunches]: (jwt) =>
      `${config.timeLaborDailyURL}/Client/UnmatchablePunchEvents.aspx?jwt=${jwt}&embedded=true&embedas=tc`,
    [route.logins]: (jwt) => `${config.masterDataUIURL}/logins.html?jwt=${jwt}`,
    [route.multiCompanyAssignment]: (jwt) =>
      `${config.masterDataUIURL}/multiCompanyAssignments.html?jwt=${jwt}`,
    [route.updatePersonalInfo]: (jwt) =>
      `${config.masterDataUIURL}/updatePersonalInfo.html?jwt=${jwt}`,
    [route.resetPartnerPassword]: (jwt) =>
      `${config.masterDataUIURL}/updatePersonalInfo.html?jwt=${jwt}&resetPassword=resetPassword`,
    [route.suggestionBox]: () => 'https://wfhsuggestionbox.ideas.aha.io/',
    [route.companyMessages]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/companyMessages.html?jwt=${jwt}`,
    [route.billing]: (jwt) =>
      `${config.masterDataUIURL}/billing.html?jwt=${jwt}`,
    [route.companyDocsAndLinks]: (jwt) =>
      `${config.hubComponentsEmbedUrl}/companyLinksAndDocs.html?jwt=${jwt}`,
    [route.inAppBilling]: (jwt) =>
      `${config.masterDataUIURL}/billingAdministration.html?jwt=${jwt}#/billing/paymenthistory`,
    [route.trialAdmin]: (jwt) =>
      `${config.masterDataUIURL}/billingAdministration.html?jwt=${jwt}#/billing/trialhistory`,
    [route.groupManagement]: (jwt) =>
      `${config.masterDataUIURL}/groupManagement.html?jwt=${jwt}`,
    [route.credmgmt]: (jwt) =>
      `${config.partnerURL}/index-credentialmanagement.html?jwt=${jwt}`,
  };

  const routeURL = routeOptions[anchor?.route] ?? {};
  const jwtToken = anchor?.jwt || token;
  return buildUrl(routeURL, jwtToken, anchor);
}

// #######################################
// ### Helper methods for building URLs
// #######################################

function buildUrl(routeURL, jwtToken, anchor) {
  if (!isFunction(routeURL) || !hasValue(jwtToken)) {
    console.error('No URL found for anchor.');
    return throwErrorWithStatusCode(404);
  }

  const url = routeURL(jwtToken, anchor);
  if (!isValidUrl(url)) {
    console.error('No URL found for anchor.');
    return throwErrorWithStatusCode(404);
  }

  const deviceId = analytics.getDeviceId();
  const sessionId = analytics.getSessionId();

  if (hasValue(deviceId) && hasValue(sessionId)) {
    return addAnalyticsParamsToUrl(url, deviceId, sessionId);
  }

  return url;
}

// Confirm that the URL is valid and not undefined
// by checking string value and ensuring it does not start with 'undefined'
function isValidUrl(url) {
  return hasValue(url) && !url.startsWith('undefined');
}

// Add analytics parameters to the URL if they exist
function addAnalyticsParamsToUrl(url, deviceId, sessionId) {
  const urlObj = new URL(url);
  urlObj.searchParams.set('ampDeviceId', deviceId);
  urlObj.searchParams.set('ampSessionId', sessionId);
  return urlObj.toString();
}
